import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import type { WebhookEndpoint, WebhookEventInfo } from '../../lib/api/operatorConsole';

interface Props {
  endpoint: WebhookEndpoint;
  availableEvents: WebhookEventInfo[];
  busy: boolean;
  onSave: (endpoint: WebhookEndpoint, eventTypes: string[]) => Promise<boolean>;
  onToggleStatus: (endpoint: WebhookEndpoint) => void;
  onTest: (endpoint: WebhookEndpoint) => void;
  onDelete: (endpoint: WebhookEndpoint) => void;
}

function health(endpoint: WebhookEndpoint): string {
  if (endpoint.status === 'paused') {
    return 'Paused — nothing is being sent here. Events that fire meanwhile are recorded as skipped.';
  }
  if (endpoint.consecutive_failures > 0) {
    return `${endpoint.consecutive_failures} failed attempt${
      endpoint.consecutive_failures === 1 ? '' : 's'
    } in a row${endpoint.last_success_at ? `, last success ${new Date(endpoint.last_success_at).toLocaleString()}` : ''}`;
  }
  if (endpoint.last_success_at) {
    return `Last delivered ${new Date(endpoint.last_success_at).toLocaleString()}`;
  }
  return 'Nothing delivered yet';
}

export function EndpointRow({
  endpoint,
  availableEvents,
  busy,
  onSave,
  onToggleStatus,
  onTest,
  onDelete,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(endpoint.event_types);
  const paused = endpoint.status === 'paused';

  function toggle(type: string) {
    setDraft((current) =>
      current.includes(type) ? current.filter((t) => t !== type) : [...current, type],
    );
  }

  async function save() {
    const ok = await onSave(endpoint, draft);
    if (ok) setEditing(false);
  }

  return (
    <li className="border-b border-[var(--co-border)] px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-[13px] font-medium text-[var(--co-text)]">
              {endpoint.url}
            </span>
            {paused && (
              <span className="rounded-full bg-[var(--co-bg)] px-2 py-[2px] text-[11px] text-[var(--co-muted)]">
                paused
              </span>
            )}
          </div>

          {endpoint.description && (
            <div className="mt-1 text-[12px] text-[var(--co-text)]">{endpoint.description}</div>
          )}

          <div className="mt-1 text-[12px] text-[var(--co-muted)]">
            {endpoint.event_types.length === 0
              ? 'Subscribed to nothing — it will never be called'
              : endpoint.event_types.join(', ')}
          </div>

          <div className="mt-1 text-[11px] text-[var(--co-muted)]">{health(endpoint)}</div>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          <Button variant="ghost" disabled={busy} onClick={() => onTest(endpoint)}>
            Send test
          </Button>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              setDraft(endpoint.event_types);
              setEditing((value) => !value);
            }}
          >
            {editing ? 'Cancel' : 'Edit events'}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => onToggleStatus(endpoint)}>
            {paused ? 'Resume' : 'Pause'}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => onDelete(endpoint)}>
            Delete
          </Button>
        </div>
      </div>

      {editing && (
        <div className="mt-3 rounded-lg border border-[var(--co-border)] p-3">
          <div className="flex flex-col gap-2">
            {availableEvents.map((event) => (
              <label key={event.type} className="flex items-start gap-2 text-[12px]">
                <input
                  type="checkbox"
                  checked={draft.includes(event.type)}
                  onChange={() => toggle(event.type)}
                  disabled={busy}
                  className="mt-[2px]"
                />
                <span>
                  <code className="text-[var(--co-text)]">{event.type}</code>
                  <span className="block text-[11px] text-[var(--co-muted)]">
                    {event.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
          <div className="mt-3">
            <Button disabled={busy} onClick={() => void save()}>
              Save
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
